import { z } from 'zod';
import { MissingResourceError } from '#api/errors.ts';
import { useSuspenseHostQuery } from '#api/hosts.queries.ts';
import { Link } from '#components/atoms/link/Link.tsx';
import { FullpageLayout } from '#components/patterns/layouts/fullpage/FullpageLayout.tsx';
import { PageCloser } from '#components/patterns/page-closer/PageCloser.tsx';
import { PageHeader } from '#components/patterns/page-header/PageHeader.tsx';
import { useZodParams } from '#hooks/useZodParams.ts';
import { hostsListTo } from '#screens/hosts/listLocation.ts';
import { hostKind } from '#util/host.ts';
import { fromArray, Titled } from '#util/title.ts';
import { DebugConsole } from '../console/DebugConsole.tsx';
import { DebugHeader } from '../header/DebugHeader.tsx';
import { useLogWorker } from '../useLogWorker.ts';
import styles from './DebugPage.module.css';

export function DebugPage() {
  const { hostId } = useZodParams({
    hostId: z.string().refine((id): id is `host-${string}` => id.startsWith('host-')),
  });
  const {
    data: { data: host },
    dataUpdatedAt,
    error,
    isFetching,
  } = useSuspenseHostQuery(hostId);
  const { status, logs, errors, sessionStart, sessionEnd, startStream } = useLogWorker(hostId);
  const listTo = hostsListTo(hostKind(host));

  // Check if this has been deleted (e.g. in a different tab)
  if (error && error instanceof MissingResourceError && !isFetching) {
    throw error;
  }

  return (
    <Titled title={(title) => fromArray(['Debug', host.name, title])}>
      <FullpageLayout
        header={
          <PageHeader
            title={
              <>
                <Link to={listTo} className="a">
                  Hosts
                </Link>
                {' / '}
                <Link to={`/hosts/${host.id}`} className="a">
                  {host.name}
                </Link>
                {' / '}
                Debug
              </>
            }
          >
            <PageCloser fallbackTo={`/hosts/${host.id}`} />
          </PageHeader>
        }
      >
        <div className={styles.DebugPage}>
          <DebugHeader host={host} dataUpdatedAt={new Date(dataUpdatedAt)} />
          <DebugConsole
            hostId={hostId}
            logs={logs}
            errors={errors}
            className={styles.DebugPage_logStream}
            status={status}
            sessionStart={sessionStart}
            sessionEnd={sessionEnd}
            onStartStream={() => {
              startStream();
            }}
          />
        </div>
      </FullpageLayout>
    </Titled>
  );
}
